import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;
    ${
  '' /* column-count: 3; This doesn't do anything. Maybe look at the absolute page constraints? */
}

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      background: transparent;
      outline: 0;

      &:after {
        top: 15px;
        left: 15px;
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 20px;
      left: 20px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'DevOps & CI/CD: Kubernetes, AWS, GitHub Actions, Go, Docker',
    'Project Management: Agile Methodologies, Jira, Trello, Scrum, Gantt Charts ',
    'Frontend Development: TypeScript, React.js, HTML/CSS',
    'Backend Development: Express.js, Node.js, Flask',
    'Mobile Development: Kotlin, Flutter, Android Studio',
    'AI/Machine Learning: Python, Keras, TensorFlow, PyTorch',
    'Object-Oriented Programming',
    'JavaScript (ES6+)',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! My name is Jon and I make stuff happen. Despite enjoying the technical aspects
              of software development, I've discovered that I have a ton of fun tackling the
              challenges that come with Project Management, causing me to think{' '}
              <i>Technical Project Management</i> is that happy medium I'm looking to achieve.
            </p>

            <p>
              I discovered my passion for CS and started my journey in 2019 at{' '}
              <a href="https://www.ucf.edu">The University of Central Florida</a>, where I became
              the <a href="https://club.knighthacks.org/">Director of Finance</a> in the largest
              technology club there- helping to plan an annual hackathon with hundreds of
              participants from all over the country!
	      
	      I'm now the president of the Association for Computing Machinery here at UCF, and
	      doing my very best to promote accessibility to career resources and workshops.
            </p>

            <p>
              Fast-forward to today, and I’ve had the privilege of interning at{' '}
              <a href="https://us.mullenlowe.com/">a fortune 100 corporation</a>,{' '}
              <a href="https://www.docker.com/">a start-up</a>, and{' '}
              <a href="https://www.apple.com/">a defense company</a>. My main focus these days is
              learning everything I can about becoming a competent project manager and keeping my
              technical skills sharp.
            </p>

            <p>Here are a few technologies I’ve been working with recently:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
